// Per-probe telemetry charts for one monitor (PRD §6.1 inspector:
// 1h / 24h / 7d / 30d served from raw / 5m / 1h data).
import { useMemo, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";

import {
  useGetMonitorTelemetry, type GetMonitorTelemetryResolution, type Probe, type TelemetryPoint,
} from "@/api/generated";
import { Sparkline, TimeSeries, type Series } from "@/components/charts/TimeSeries";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useNow } from "@/lib/now";

export const RANGES = {
  "1h": { ms: 3_600_000, resolution: "raw" },
  "24h": { ms: 86_400_000, resolution: "5m" },
  "7d": { ms: 7 * 86_400_000, resolution: "1h" },
  "30d": { ms: 30 * 86_400_000, resolution: "1h" },
} as const satisfies Record<string, { ms: number; resolution: GetMonitorTelemetryResolution }>;

export type Range = keyof typeof RANGES;

export const METRICS = {
  rtt: { label: "RTT", unit: " ms", pick: (p: TelemetryPoint) => p.rtt_avg_ms ?? null },
  loss: { label: "Loss", unit: "%", pick: (p: TelemetryPoint) => p.loss_pct ?? null },
  jitter: { label: "Jitter", unit: " ms", pick: (p: TelemetryPoint) => p.jitter_ms ?? null },
} as const;

export type Metric = keyof typeof METRICS;

const PALETTE = ["#6d78e8", "#22d3ee", "#f472b6", "#a3e635", "#fbbf24", "#fb7185", "#34d399", "#c084fc"];

/** Window start rounded to the minute, so the query key (and data) move once a minute. */
export function useWindowStart(rangeMs: number): string {
  const minute = Math.floor(useNow() / 60_000);
  return useMemo(() => new Date(minute * 60_000 - rangeMs).toISOString(), [minute, rangeMs]);
}

export function useTelemetry(monitorId: string, range: Range) {
  const from = useWindowStart(RANGES[range].ms);
  return useGetMonitorTelemetry(
    monitorId,
    { from, resolution: RANGES[range].resolution },
    { query: { placeholderData: keepPreviousData, staleTime: 30_000 } },
  );
}

export function toSeries(points: TelemetryPoint[], metric: Metric, probes: Map<string, Probe>): Series[] {
  const byProbe = new Map<string, Array<[number, number | null]>>();
  for (const p of points) {
    let arr = byProbe.get(p.probe_id);
    if (!arr) byProbe.set(p.probe_id, (arr = []));
    arr.push([new Date(p.time).getTime(), METRICS[metric].pick(p)]);
  }
  // Customers do not know probe names: their lines are "Probe 1…n".
  return [...byProbe.entries()]
    .sort(([a], [b]) => (probes.get(a)?.name ?? a).localeCompare(probes.get(b)?.name ?? b))
    .map(([id, pts], i) => ({ label: probes.get(id)?.name ?? `Probe ${i + 1}`, color: PALETTE[i % PALETTE.length], points: pts }));
}

export function RangeToggle({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <ToggleGroup type="single" size="sm" variant="outline" value={value} onValueChange={(v) => v && onChange(v as Range)}>
      {(Object.keys(RANGES) as Range[]).map((r) => <ToggleGroupItem key={r} value={r} className="tabular px-2 text-xs">{r}</ToggleGroupItem>)}
    </ToggleGroup>
  );
}

export function MetricToggle({ value, onChange }: { value: Metric; onChange: (m: Metric) => void }) {
  return (
    <ToggleGroup type="single" size="sm" variant="outline" value={value} onValueChange={(v) => v && onChange(v as Metric)}>
      {(Object.keys(METRICS) as Metric[]).map((m) => <ToggleGroupItem key={m} value={m} className="px-2 text-xs">{METRICS[m].label}</ToggleGroupItem>)}
    </ToggleGroup>
  );
}

export function TelemetryChart({
  monitorId, probes, range, metric, height = 180,
}: {
  monitorId: string; probes: Map<string, Probe>; range: Range; metric: Metric; height?: number;
}) {
  const q = useTelemetry(monitorId, range);
  const series = useMemo(() => toSeries(q.data?.points ?? [], metric, probes), [q.data, metric, probes]);
  if (q.isLoading) return <div style={{ height }} className="animate-pulse rounded-md bg-muted/40" />;
  if (q.error) return <p className="text-sm text-destructive">Could not load telemetry.</p>;
  if (series.length === 0) {
    return <div style={{ height }} className="flex items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">No samples in this range yet.</div>;
  }
  if (series.every((s) => s.points.every(([, v]) => v === null))) {
    return (
      <div style={{ height }} className="flex items-center justify-center rounded-md border border-dashed px-4 text-center text-sm text-muted-foreground">
        No {METRICS[metric].label} values in this range: every sample failed. Loss shows the outage.
      </div>
    );
  }
  return <TimeSeries series={series} height={height} unit={METRICS[metric].unit} />;
}

/** Monitor detail / inspector chart block with its own range and metric switches. */
export function TelemetryPanel({
  monitorId, probes, height, down,
}: {
  monitorId: string; probes: Map<string, Probe>; height?: number; down?: boolean;
}) {
  const [range, setRange] = useState<Range>("1h");
  // A monitor that is down has no RTT to draw; start on loss instead.
  const [metric, setMetric] = useState<Metric>(down ? "loss" : "rtt");
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <MetricToggle value={metric} onChange={setMetric} />
        <RangeToggle value={range} onChange={setRange} />
      </div>
      <TelemetryChart monitorId={monitorId} probes={probes} range={range} metric={metric} height={height} />
    </div>
  );
}

/** Last hour of RTT (worst probe per 2-minute bucket) for dense lists. */
export function MonitorSparkline({ monitorId, className }: { monitorId: string; className?: string }) {
  const q = useTelemetry(monitorId, "1h");
  const values = useMemo(() => {
    const buckets: Array<number | null> = new Array(30).fill(null);
    const pts = q.data?.points ?? [];
    if (pts.length === 0) return buckets;
    const end = q.dataUpdatedAt;
    for (const p of pts) {
      const i = 29 - Math.floor((end - new Date(p.time).getTime()) / 120_000);
      if (i < 0 || i > 29 || p.rtt_avg_ms == null) continue;
      buckets[i] = Math.max(buckets[i] ?? 0, p.rtt_avg_ms);
    }
    return buckets;
  }, [q.data, q.dataUpdatedAt]);
  return (
    <span className={className} title="RTT, last hour (worst probe)">
      <Sparkline values={values} />
    </span>
  );
}
